import {
  Center,
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  Pagination,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { IconCheck } from "@tabler/icons-react"
import { InformationRequestStatus } from "@tfg/db"
import { Button } from "@tfg/ui"
import { Fragment, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { TraceabilityResponses } from "@tfg/types/endpoints/traceability"

import { FindOperationChainsResponseDto } from "~/gen/api/types"
import { CheckList } from "~/pages/SupplierPortal/TraceabilityRequests/TraceabilityRequestsList.tsx"
import {
  useListTraceabilityRequests,
  useSubmitRequestsMutation,
} from "~/pages/SupplierPortal/TraceabilityRequests/hooks.ts"
import {
  RequestWithOpItem,
  canSubmitRequest,
  getComponentComposition,
  requestsToRequestsWithOp,
} from "~/pages/SupplierPortal/TraceabilityRequests/utils.ts"
import { useAuth } from "~/store/auth-context.tsx"
import { useGetTranslationCallback } from "~/utils/translations"

interface Props {
  opened: boolean
  onClose: () => void
  operationChains: FindOperationChainsResponseDto[]
}
const REQUESTS_PER_PAGE = 10

export const SubmitRequestsModal = ({
  opened,
  onClose,
  operationChains,
}: Props) => {
  const { t } = useTranslation(["traceability"])

  const { user } = useAuth()

  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: REQUESTS_PER_PAGE,
  })

  const { data, isLoading: isRequestsLoading } = useListTraceabilityRequests({
    pagination,
    filters: {
      status: InformationRequestStatus.OPENED,
    },
  })

  const [certify, setCertify] = useState<boolean>(false)
  const closeModal = () => {
    onClose()
    setCertify(false)
    setCheckList({})
  }

  const [checkList, setCheckList] = useState<CheckList>({})

  const { mutate: submitRequests, isLoading } = useSubmitRequestsMutation({
    onSuccess: (_data, variables) => {
      setCheckList({})
      notifications.show({
        message: t("submitModal.confirmation", {
          count: variables.requests.length,
        }),
      })
      onClose()
    },
    onError: () => {
      notifications.show({ message: t("submitModal.error"), color: "red" })
      setCertify(false)
    },
  })

  const handleSubmitRequests = (requests: RequestWithOpItem[]) => {
    submitRequests({
      requests: requests.map((request) => ({
        id: request.original.id,
        request: {
          operations: request.opItems.map((item) => {
            return {
              operationId: item.operation?.id ?? -1, // Very lazy way of not having to do crazy typing
              reference: item.operation?.reference ?? item.item.reference,
            }
          }),
        },
      })),
    })
  }

  const requests = requestsToRequestsWithOp({
    requests: data?.data,
    operationChains,
  })

  const validRequests = requests.filter((request) => {
    if (!user) return []

    return canSubmitRequest(request.opItems, user)
  })

  const allChecked: CheckList = useMemo(() => {
    const checked: CheckList = {}
    validRequests?.forEach((request) => (checked[request.original.id] = true))
    return checked
  }, [validRequests])

  const [searchingForValidRequests, setSearchingForValidRequests] =
    useState(false)

  useEffect(() => {
    const lastPage = Math.ceil((data?.total ?? 0) / REQUESTS_PER_PAGE) - 1
    if (validRequests.length === 0 && pagination.page < lastPage) {
      if (
        pagination.page <
        Math.ceil((data?.total ?? 0) / REQUESTS_PER_PAGE) - 1
      ) {
        setSearchingForValidRequests(true)
        setPagination((prev) => ({
          page: prev.page + 1,
          pageSize: prev.pageSize,
        }))
      }
    }
    if (validRequests.length || pagination.page === lastPage) {
      setSearchingForValidRequests(false)
    }
  }, [validRequests, data?.total, pagination.page])

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title={
        <Text size="lg">
          <strong>{t("submitModal.title")}</strong>
        </Text>
      }
      returnFocus={false}
      size="100vw"
    >
      <Stack>
        <Group justify="left">
          <Button variant="secondary" onClick={() => setCheckList(allChecked)}>
            {t("selectAll")}
          </Button>
          <Button variant="secondary" onClick={() => setCheckList({})}>
            {t("deselectAll")}
          </Button>
          <Pagination
            size="sm"
            value={pagination.page + 1}
            onChange={(page) =>
              setPagination((prev) => ({ ...prev, page: page - 1 }))
            }
            total={Math.ceil((data?.total ?? 0) / REQUESTS_PER_PAGE)}
          />
        </Group>
        {searchingForValidRequests && (
          <Center>
            <Text>{t("submitModal.searching")}</Text>
          </Center>
        )}
        <ScrollArea
          style={{ width: "100%" }}
          h="calc(100vh - 400px)"
          type="always"
          offsetScrollbars
        >
          <LoadingOverlay
            visible={isLoading || isRequestsLoading}
            overlayProps={{ blur: 2 }}
          />
          {validRequests.length ? (
            <Table verticalSpacing="xs" highlightOnHover={true}>
              <thead>
                <tr>
                  <th />
                  <th>
                    <Title order={4}>{t("submitModal.type")}</Title>
                  </th>
                  <th>
                    <Title order={4}>{t("submitModal.reference")}</Title>
                  </th>
                  <th>
                    <Title order={4}>{t("submitModal.composition")}</Title>
                  </th>
                </tr>
              </thead>
              <tbody>
                {validRequests.map((request) => (
                  <Row
                    key={request.original.id}
                    request={request}
                    checked={!!checkList[request.original.id]}
                    update={(id: number) =>
                      setCheckList({
                        ...checkList,
                        [id]: !checkList[id],
                      })
                    }
                  />
                ))}
              </tbody>
            </Table>
          ) : (
            <Group justify="center">
              <Text c="gray">{t("submitModal.noRequests")}</Text>
            </Group>
          )}
        </ScrollArea>
        <Group justify="right">
          <Checkbox
            label={t("submitModal.confidenceStatement")}
            onChange={(event) => {
              setCertify(event.currentTarget.checked)
            }}
          />
          <Button
            icon={<IconCheck />}
            onClick={() => {
              handleSubmitRequests(
                validRequests.filter((request) => {
                  return checkList[request.original.id]
                })
              )
            }}
            disabled={
              !certify || !Object.values(checkList).filter((x) => x).length
            }
          >
            {t("submitModal.submitButton", {
              itemCount: Object.values(checkList).filter((x) => x).length,
            })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

type RowProps = {
  request: RequestWithOpItem
  checked: boolean | undefined
  update: (val: number) => void
  color?: string
}

function Row({ request, checked, update, color = "green" }: RowProps) {
  return (
    <tr onClick={() => update(request.original.id)}>
      <td>
        <Checkbox
          onChange={() => update(request.original.id)}
          checked={checked}
          color={color}
        />
      </td>
      <td>
        <b>
          <Text style={{ textTransform: "capitalize" }}>
            {request.original.item.type}
          </Text>
        </b>
      </td>
      <td>{request.original.item.reference}</td>
      <td>
        <Text>
          <ItemComposition item={request.original.item} />
        </Text>
      </td>
    </tr>
  )
}

type ItemCompositionProps = {
  item: TraceabilityResponses.TraceabilityRequestItem
}
export function ItemComposition({ item }: ItemCompositionProps) {
  const getTranslation = useGetTranslationCallback()

  switch (item.type) {
    case "product": {
      return (
        <>
          {item.children.map((c, index) => (
            <Fragment key={index}>
              <b>{c.reference}</b>: {getComponentComposition(c, getTranslation)}
              <br />
            </Fragment>
          ))}
        </>
      )
    }
    case "component": {
      return <>{getComponentComposition(item, getTranslation)}</>
    }
    case "material": {
      return <>{item.reference} </>
    }
  }
}
